import React, { useCallback } from 'react';

import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useAppSelector } from '@pkg/hooks';

import { Groups } from '@app/client';
import { AUTH_SIGN_IN } from '@app/routes/paths/auth';

import { useUpdateCache } from '../utilities';

import { FollowButton, FollowingButton } from './styles';

interface GroupHeaderButtonProps {
    isFollowing?: boolean;
    groupId?: string;
}

export const GroupHeaderButton = ({ isFollowing, groupId, ...others }: GroupHeaderButtonProps) => {
    const { t } = useTranslation(['groups']);
    const isRegisteredUser = useAppSelector((state) => state.user.isRegisteredUser);
    const navigate = useNavigate();

    const updateCache = useUpdateCache(groupId);

    const { mutate: followGroup, isLoading: isLoadingFollow } = useMutation(
        async () => Groups.callFollowGroup({ groupId }),
        { onSuccess: updateCache },
    );

    const { mutate: unfollowGroup, isLoading: isLoadingUnfollow } = useMutation(
        async () => Groups.callUnfollowGroup({ groupId }),
        { onSuccess: updateCache },
    );

    const isLoading = isLoadingFollow || isLoadingUnfollow;

    const handleOnClick = useCallback(() => {
        if (!isRegisteredUser) {
            navigate(AUTH_SIGN_IN);
            return;
        }

        if (isFollowing) {
            unfollowGroup();
        } else {
            followGroup();
        }
    }, [isRegisteredUser, isFollowing, navigate, unfollowGroup, followGroup]);

    if (isFollowing === true) {
        return (
            <FollowingButton onClick={handleOnClick} disabled={isLoading || !groupId} {...others}>
                <span>{t('groups_following')}</span>
                <span>{t('groups_unfollow')}</span>
            </FollowingButton>
        );
    }

    return (
        <FollowButton disabled={isLoading || !groupId} onClick={handleOnClick} {...others}>
            {t('groups_follow')}
        </FollowButton>
    );
};
